import { Link as WebUILink } from '@stenajs-webui/elements';
import { type FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { stringifyManagePageUrlParams } from '../../../common/booking-filters/urlFilterState';
import type { Sailing } from '../../../common/graphql/fragments/gql/Sailing';
import { useBackgroundLocation } from '../../../common/home/components/UseBackgroundLocation';
import { useLocalDateFormat } from '../../../common/hooks/useLocalDateFormat';
import { routeCreators } from '../../../common/routes/AppRoutes';
import { isOnManagePage } from '../../../common/routes/routeUtils';
import { trackEvent } from '../../../common/tracking/trackerService';
import { createDepartureFilter } from '../../../common/utils/createDepartureFilter';
import { Link } from '../../../common-ui/Link';
import { formatSailingLinkText } from '../../../common-ui/sailing-info/utils/formatSailingLinkText';
import { setFilter } from '../../../services/booking-filter/filterActions';
import { getSorting } from '../../../services/sorting/sortingReducer';
import { useAppDispatch } from '../../../store/appDispatch';
import { useNotificationContext } from '../context/NotificationContext';

interface SailingLinkProps {
  sailing: Sailing;
}

export const SailingLink: FC<SailingLinkProps> = ({ sailing }) => {
  const { trackerCategory, onRequestClose } = useNotificationContext();
  const dispatch = useAppDispatch();
  const location = useBackgroundLocation();
  const dateFormat = useLocalDateFormat();
  const sorting = useSelector(getSorting);
  const filterState = useMemo(
    () => createDepartureFilter(sailing.departureDate, sailing.route.code, sailing.departureTime),
    [sailing.departureDate, sailing.departureTime, sailing.route.code],
  );

  const url = useMemo(
    () => `${routeCreators.bookings()}?${stringifyManagePageUrlParams(filterState, sorting)}`,
    [filterState, sorting],
  );

  return (
    <Link
      className={'t_booking_toast_show_departure_link'}
      onClick={(_e, isModified) => {
        onRequestClose?.();
        trackEvent({ category: trackerCategory, action: 'Show departure' });
        if (!isModified) {
          dispatch(setFilter(filterState));
        }
      }}
      to={url}
      replace={isOnManagePage(location)}
      render={props => (
        <WebUILink {...props}>
          {formatSailingLinkText(sailing, sailing.route.code, dateFormat)}
        </WebUILink>
      )}
    />
  );
};
